/**
 * Lingue dell'interfaccia: elenco, rilevazione e preferenza salvata.
 *
 * Sta fuori da `index.ts` perché è la parte **senza i18next**: funzioni pure e
 * due accessi al `localStorage`, che si provano senza montare nulla e senza
 * inizializzare la libreria. La regola che sceglie la lingua alla prima visita
 * è la stessa che decide se un visitatore italiano vede l'italiano, quindi va
 * potuta leggere da sola.
 *
 * La preferenza esplicita vince sempre sul browser: chi ha scelto una lingua
 * l'ha scelta, anche da un computer configurato in un'altra.
 */

/** Lingue in cui la dashboard è tradotta, nell'ordine in cui compaiono nel selettore. */
export const LANGUAGES = ['it', 'en', 'de', 'es', 'ru', 'ko', 'th'] as const;

/** Codice di una lingua supportata. */
export type Language = (typeof LANGUAGES)[number];

/**
 * Lingua usata quando né la preferenza salvata né il browser dicono altro.
 *
 * È l'italiano perché i testi italiani sono la fonte: l'inglese è una
 * traduzione, e una traduzione non è mai il valore di ripiego.
 */
export const DEFAULT_LANGUAGE: Language = 'it';

/** Chiave sotto cui il browser ricorda la lingua scelta. */
/** Lingua per un browser che non chiede nessuna delle lingue della dashboard. */
export const UNKNOWN_BROWSER_LANGUAGE: Language = 'en';

export const LANGUAGE_STORAGE_KEY = 'institor.language';

/**
 * Indica se un valore qualsiasi è il codice di una lingua supportata.
 *
 * @param value Valore da verificare, tipicamente letto dal browser.
 * @returns True se il valore è una lingua supportata.
 */
export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * Riduce un tag di lingua BCP 47 a una lingua supportata.
 *
 * `en-GB`, `en_US` e `EN` sono tutti inglese: si guarda la sola sottoparte
 * primaria, perché non distinguiamo le varianti regionali.
 *
 * @param tag Tag di lingua, per esempio `it-CH`.
 * @returns La lingua supportata, oppure `null` se non ne conosciamo nessuna.
 */
export function languageOf(tag: string | undefined | null): Language | null {
  if (typeof tag !== 'string') {
    return null;
  }
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0];
  return isLanguage(primary) ? primary : null;
}

/**
 * Sceglie la lingua a partire dall'elenco di preferenze del browser.
 *
 * Si scorre `navigator.languages` nell'ordine dichiarato e si prende la prima
 * lingua che sappiamo parlare; se non ce n'è nessuna vale l'inglese
 * (`UNKNOWN_BROWSER_LANGUAGE`, decisione del 2026-10-31): per chi legge in
 * francese o in portoghese l'inglese è più utile dell'italiano.
 *
 * @param candidates Tag di lingua in ordine di preferenza.
 * @returns La lingua da usare.
 */
export function detectLanguage(candidates: readonly string[]): Language {
  for (const candidate of candidates) {
    const language = languageOf(candidate);
    if (language !== null) {
      return language;
    }
  }
  return UNKNOWN_BROWSER_LANGUAGE;
}

/**
 * Legge la lingua scelta in una visita precedente.
 *
 * Il `localStorage` non è sempre disponibile — finestra anonima, dati del sito
 * bloccati, `SecurityError` in un iframe — e leggerlo può sollevare invece di
 * restituire `null`. Un'eccezione qui non deve impedire di mostrare la
 * dashboard: senza preferenza si ricade sul browser.
 *
 * @returns La lingua salvata, oppure `null` se non c'è o non è leggibile.
 */
export function readStoredLanguage(): Language | null {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Ricorda la lingua scelta per le visite successive.
 *
 * È una comodità, non un dato: se la scrittura fallisce la lingua resta attiva
 * per questa sessione e basta.
 *
 * @param language Lingua scelta dall'utente.
 */
export function storeLanguage(language: Language): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Spazio esaurito o accesso negato: la scelta vale comunque fino al
    // ricaricamento della pagina.
  }
}

/**
 * Lingua con cui avviare l'applicazione.
 *
 * Ordine: preferenza salvata, poi le lingue del browser, poi il default.
 *
 * @returns La lingua iniziale.
 */
export function initialLanguage(): Language {
  const stored = readStoredLanguage();
  if (stored !== null) {
    return stored;
  }
  const navigatorLanguages =
    typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
  return detectLanguage(navigatorLanguages.filter((tag): tag is string => typeof tag === 'string'));
}

/**
 * Locale di `Intl` per date e numeri nella lingua dell'interfaccia.
 *
 * Il tailandese chiede il calendario gregoriano in modo esplicito: senza, `th-TH` conta gli
 * anni dell'era buddista (2569 invece di 2026).
 */
const INTL_LOCALES: Readonly<Record<Language, string>> = {
  it: 'it-IT',
  en: 'en-US',
  de: 'de-DE',
  es: 'es-ES',
  ru: 'ru-RU',
  ko: 'ko-KR',
  th: 'th-TH-u-ca-gregory',
};

/**
 * Locale di `Intl` di una lingua.
 *
 * @param language Lingua dell'interfaccia, anche non riconosciuta.
 * @returns Il tag BCP 47 da passare a `Intl`; l'italiano per una lingua sconosciuta.
 */
export function intlLocale(language: string): string {
  return isLanguage(language) ? INTL_LOCALES[language] : INTL_LOCALES[DEFAULT_LANGUAGE];
}
